import React, { useContext, useEffect, useRef, useState } from "react";
import { createIncrementalAnalytic } from "../../shared/services/analytics/analytics.service";
import { AnalyticTypesEnum } from "../../shared/enums/analytics-types.enum";
import AuthContext from "../../shared/context/authContext/AuthContext";
import { AnalyticOriginEnum } from "../../shared/enums/analytics-origin.enum";

const LOCAL_STORAGE_KEY = "AnalyticsStartTime";
const INACTIVE_MILI = 59000;
const UPDATE_EVERY_MILI = 60000;

const useActive = (duration: number): boolean => {
    const [inactive, setInactive] = useState(true);
    const timeoutRef = useRef<any>();

    function handleUserActivity() {
        setInactive(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setInactive(false), duration);
    }

    useEffect(() => {
        const events = ["click", "scroll"];

        for (let event of events) {
            window.addEventListener(event, handleUserActivity);
        }

        return () => {
            for (let event of events) {
                window.removeEventListener(event, handleUserActivity);
            }
        };
        // Mount-only: the add and the remove use the same handleUserActivity, and depending on it would
        // re-register both listeners every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return inactive;
};

const AnalyticsProvider = ({ children }: any) => {
    const { state } = useContext(AuthContext);

    const active = useActive(INACTIVE_MILI);

    const [startTime, setStartTime] = useState<number>(0);
    const [isRunning, setIsRunning] = useState(true);

    // The interval is created once, so it reads the session through a ref. On a hard refresh this
    // provider mounts before the session is restored, and a closed-over state would never see the
    // user. Depending on state instead would restart the interval and reset its 60-second clock.
    const sessionRef = useRef(state);
    sessionRef.current = state;

    const updateAnalytics = async () => {
        try {
            await createIncrementalAnalytic({
                type: AnalyticTypesEnum.ACTIVE_MINUTES,
                origin: AnalyticOriginEnum.WEB,
                count: 1,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleStart = () => {
        const now = Date.now();
        setStartTime(now);
        setIsRunning(true);
        localStorage.setItem(LOCAL_STORAGE_KEY, now.toString());
    };

    const handleStop = () => {
        setIsRunning(false);
        setStartTime(0);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    };

    useEffect(() => {
        if (process.env.REACT_APP_ENV !== "local") {
            if (state && state.user) {
                if (active) {
                    handleStart();
                } else {
                    handleStop();
                }
            }
        }
        // Runs when activity toggles. startTime and AnalyticsStartTime are only written, never read, and
        // recording does not depend on this path.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (process.env.REACT_APP_ENV !== "local") {
            // Actualizar el tiempo activo si el temporizador está en marcha
            let timer: any;
            if (isRunning) {
                timer = setInterval(() => {
                    if (sessionRef.current?.user) {
                        updateAnalytics();
                    }
                }, UPDATE_EVERY_MILI);
            }
            return () => clearInterval(timer);
        }
        // state is read through sessionRef so the interval is not restarted when the session changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRunning, startTime]);

    return <></>;
};

export default AnalyticsProvider;
